<template>
  <div>
    <b-card>
      <TitleTable titleTable="Membership discount exemptions" iconTable="fas fa-percent" />
      <p class="text-muted mb-3">
        Ítems que no reciben el descuento de membresía sobre lo no cubierto. Solo admin y superadmin.
      </p>

      <b-alert v-if="!canManage" show variant="warning">
        Esta pantalla es solo para admin y superadmin.
      </b-alert>

      <template v-else>
        <b-alert v-if="errorMessage" show variant="danger" dismissible @dismissed="errorMessage = null">
          {{ errorMessage }}
        </b-alert>

        <b-row class="mb-3">
          <b-col cols="12" md="4">
            <b-form-group label="Catálogo" label-for="exemption-type">
              <b-form-select id="exemption-type" v-model="catalogType" :options="catalogOptions" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="8">
            <b-form-group label="Buscar ítem" label-for="exemption-search">
              <b-form-input
                id="exemption-search"
                v-model="searchText"
                placeholder="Nombre del ítem"
                @input="scheduleSearch" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-table
          :items="searchResults"
          :fields="searchFields"
          :busy="searching"
          small
          responsive
          show-empty
          empty-text="Escribe para buscar un ítem del catálogo.">
          <template #cell(actions)="data">
            <b-button size="sm" variant="outline-primary" :disabled="savingId === data.item.id" @click="setExempt(data.item, true)">
              Marcar exento
            </b-button>
          </template>
        </b-table>

        <h5 class="mt-4 mb-2">Ítems exentos</h5>
        <b-table
          :items="exemptions"
          :fields="exemptionFields"
          :busy="loading"
          small
          responsive
          show-empty
          empty-text="No hay ítems exentos.">
          <template #cell(type)="data">
            {{ catalogLabel(data.item.type) }}
          </template>
          <template #cell(actions)="data">
            <b-button size="sm" variant="outline-danger" :disabled="savingId === data.item.id" @click="setExempt(data.item, false)">
              Quitar
            </b-button>
          </template>
        </b-table>
      </template>
    </b-card>
  </div>
</template>

<script>
import { BCard, BRow, BCol, BFormGroup, BFormSelect, BFormInput, BTable, BButton, BAlert } from 'bootstrap-vue'
import TitleTable from '@/components/TitleTable'
import exemptionsApi from '@/core/services/api/admin/consults/membershipDiscountExemptions'
import laboratories from '@/core/services/api/admin/consults/laboratories'
import laboratoryCategories from '@/core/services/api/admin/consults/laboratoryCategories'
import testsApi from '@/core/services/api/admin/consults/tests'
import proceduresApi from '@/core/services/api/admin/consults/procedures'
import treatmentsApi from '@/core/services/api/admin/consults/treatments'
import diagnosticImages from '@/core/services/api/admin/consults/diagnosticImages'
import { isMembershipPlanAdmin, membershipApiMessage } from '@/core/utils/membershipAdmin'

const CATALOGS = [
  { value: 'laboratories', text: 'Laboratories' },
  { value: 'laboratories_pack', text: 'Laboratory packages' },
  { value: 'tests', text: 'Tests' },
  { value: 'diagnostic_imagings', text: 'Diagnostic imaging' },
  { value: 'procedures', text: 'Procedures' },
  { value: 'treatments', text: 'Treatments' },
]

function rowsFrom(response) {
  if (!response) return []
  if (Array.isArray(response)) return response
  if (Array.isArray(response.data)) return response.data
  if (response.registro && Array.isArray(response.registro.data)) return response.registro.data
  if (response.data && Array.isArray(response.data.data)) return response.data.data
  if (response.data && typeof response.data === 'object') {
    return Object.entries(response.data).flatMap(([type, items]) => {
      if (!Array.isArray(items)) return []
      return items.map((item) => ({ ...item, type: item.type || type }))
    })
  }
  return []
}

export default {
  name: 'MembershipDiscountExemptions',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BTable,
    BButton,
    BAlert,
    TitleTable,
  },
  data() {
    return {
      canManage: isMembershipPlanAdmin(),
      catalogType: 'laboratories',
      catalogOptions: CATALOGS,
      searchText: '',
      searchResults: [],
      exemptions: [],
      loading: false,
      searching: false,
      savingId: null,
      errorMessage: null,
      searchTimer: null,
      searchFields: [
        { key: 'name', label: 'Ítem' },
        { key: 'price', label: 'Precio' },
        { key: 'actions', label: '' },
      ],
      exemptionFields: [
        { key: 'name', label: 'Ítem' },
        { key: 'type', label: 'Catálogo' },
        { key: 'id', label: 'Id' },
        { key: 'actions', label: '' },
      ],
    }
  },
  mounted() {
    if (this.canManage) this.loadExemptions()
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },
  methods: {
    catalogLabel(type) {
      const found = CATALOGS.find((item) => item.value === type)
      return found ? found.text : type
    },
    async loadExemptions() {
      this.loading = true
      this.errorMessage = null
      try {
        const payload = await exemptionsApi.listExemptions()
        this.exemptions = rowsFrom(payload).map((item) => ({
          id: item.id ?? item.parametrizable_id,
          name: item.name || item.item_name || item.parametrizable_name || '—',
          type: item.type || item.catalog || this.catalogType,
          price: item.price,
        }))
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo cargar la lista de exentos.')
        this.exemptions = []
      } finally {
        this.loading = false
      }
    },
    scheduleSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.searchCatalog()
      }, 300)
    },
    async searchCatalog() {
      const name = (this.searchText || '').trim()
      if (!name) {
        this.searchResults = []
        return
      }
      this.searching = true
      try {
        const payload = { name, status: true }
        let response
        if (this.catalogType === 'laboratories') response = await laboratories.laboratoryFilter(10, payload)
        else if (this.catalogType === 'laboratories_pack') response = await laboratoryCategories.categoryLabFilter(10, payload)
        else if (this.catalogType === 'tests') response = await testsApi.testFilter(10, payload)
        else if (this.catalogType === 'diagnostic_imagings') response = await diagnosticImages.diagnosticImageFilter(10, payload)
        else if (this.catalogType === 'procedures') response = await proceduresApi.procedureFilter(10, payload)
        else response = await treatmentsApi.treatmentFilter(10, payload)

        this.searchResults = rowsFrom(response).map((item) => ({
          id: item.id,
          name: item.name,
          price: item.price,
          type: this.catalogType,
        })).filter((item) => item.id != null)
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo buscar en el catálogo.')
        this.searchResults = []
      } finally {
        this.searching = false
      }
    },
    async setExempt(item, exempt) {
      if (!item || item.id == null) return
      this.savingId = item.id
      this.errorMessage = null
      try {
        await exemptionsApi.updateExemption({
          type: item.type || this.catalogType,
          id: Number(item.id),
          exempt,
        })
        await this.loadExemptions()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo actualizar el ítem.')
      } finally {
        this.savingId = null
      }
    },
  },
}
</script>
